import { getDataSourceTableKey } from 'src/constants/dataSourceTable';
import { CatalogTableColumnsBindingResult } from 'src/templates/queries/catalogPrimaryKeyTableColumns';
import {
  CATALOG_HAS_CATALOG_IRI,
  CATALOG_HAS_COLUMN_IRI,
  CATALOG_HAS_SCHEMA_IRI,
  CATALOG_HAS_TABLE_IRI,
  CATALOG_LINKED_DATA_SOURCE_IRI,
  CATALOG_LOCAL_IRI,
  CATALOG_PROVIDED_BY_IRI,
  CATALOG_PROVIDERS_IRI,
  DATA_SOURCE_PREFIX,
  LOCAL_NAMED_GRAPH_IRI,
  RDFS_LABEL_IRI,
} from 'src/templates/queries/iris';

/**
 * Queries the Catalog database for ALL column names of the given data sources'
 * tables (via `hasColumn`) — unlike `buildCatalogPrimaryKeyTableColumnsQuery`,
 * which returns only primary-key columns. Used to source editable column
 * headers when resource previews are disabled (VET-6585), so a table can still
 * be mapped without running the expensive `SELECT * ... LIMIT` preview query.
 *
 * **Call Frequency**
 *
 * Once per data source when a skip-preview connection's tables load.
 *
 * **Context**
 * - Named graphs: default graph (query uses FROM NAMED)
 * - Reasoning: always off
 */
export const buildCatalogTableColumnsQuery = (dataSourceIds: string[]) => {
  return `# Table Columns (all)
SELECT DISTINCT ?column ?columnName ?dataSource ?catalogName ?schemaName ?table ?tableName
FROM NAMED <${LOCAL_NAMED_GRAPH_IRI}>
WHERE {
  VALUES ?dataSource {
    ${dataSourceIds.map((iri) => `<${DATA_SOURCE_PREFIX}${iri}>`).join(' ')}
  }
  GRAPH <${CATALOG_PROVIDERS_IRI}> {
    ?provider <${CATALOG_LINKED_DATA_SOURCE_IRI}> ?dataSource ;
  }
  GRAPH <${CATALOG_LOCAL_IRI}> {
    ?dbms <${CATALOG_PROVIDED_BY_IRI}> ?provider
  }
  GRAPH ?dbms {
    ?dbms (<${CATALOG_HAS_CATALOG_IRI}>/<${CATALOG_HAS_SCHEMA_IRI}>/<${CATALOG_HAS_TABLE_IRI}>
      | <${CATALOG_HAS_CATALOG_IRI}>/<${CATALOG_HAS_TABLE_IRI}>
      | <${CATALOG_HAS_SCHEMA_IRI}>/<${CATALOG_HAS_TABLE_IRI}>) ?table .
    ?table <${CATALOG_HAS_COLUMN_IRI}> ?column .
    ?table <${RDFS_LABEL_IRI}> ?tableName .
    ?column <${RDFS_LABEL_IRI}> ?columnName .
    OPTIONAL {
      ?dbms <${CATALOG_HAS_CATALOG_IRI}> ?catalog .
      ?catalog <${CATALOG_HAS_SCHEMA_IRI}> ?schema .
      ?schema <${RDFS_LABEL_IRI}> ?schemaName .
      ?catalog <${RDFS_LABEL_IRI}> ?catalogName .
      ?schema <${CATALOG_HAS_TABLE_IRI}> ?table .
    }
    OPTIONAL {
      ?dbms <${CATALOG_HAS_CATALOG_IRI}> ?catalog .
      ?catalog <${CATALOG_HAS_TABLE_IRI}> ?table .
      ?catalog <${RDFS_LABEL_IRI}> ?catalogName .
    }
    OPTIONAL {
      ?dbms <${CATALOG_HAS_SCHEMA_IRI}> ?schema .
      ?schema <${CATALOG_HAS_TABLE_IRI}> ?table .
      ?schema <${RDFS_LABEL_IRI}> ?schemaName .
    }
  }
}`;
};

/**
 * Maps catalog column names to editable preview fields (value === label, so the
 * label can be renamed later). Shared by the skip-preview flows that source
 * column headers from the catalog instead of a preview query.
 */
export const catalogColumnsToFields = (
  columns: string[]
): Array<{ value: string; label: string }> =>
  columns.map((name) => ({ value: name, label: name }));

/**
 * Parses catalog column bindings into a lookup keyed by the same composite key
 * as `getDataSourceTableKey` (catalog.schema.name), mapping each table to its
 * ordered, de-duplicated column names.
 */
export const parseCatalogColumnResults = (
  rows: CatalogTableColumnsBindingResult[]
): Record<string, string[]> => {
  const columnsByTableKey: Record<string, string[]> = {};
  rows.forEach((row) => {
    const tableKey = getDataSourceTableKey({
      name: row.tableName.value,
      catalog: row.catalogName?.value,
      schema: row.schemaName?.value,
    });
    const columnName = row.columnName.value;
    const existing = columnsByTableKey[tableKey];
    if (!existing) {
      columnsByTableKey[tableKey] = [columnName];
    } else if (!existing.includes(columnName)) {
      existing.push(columnName);
    }
  });
  return columnsByTableKey;
};
